/** @format */

"use client";
import CustomButton from "@/component/Buttons/CustomButton";
import React, { useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { apiService } from "@/services/api.services";
import ReCAPTCHA from "react-google-recaptcha";
import { useRouter } from "next/navigation";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const ContactForm = () => {
  const router = useRouter();
  const recaptchaRef = useRef(null);
  const [submissionSuccessful, setSubmissionSuccessful] = useState(false);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string()
      .email("Please enter a valid email")
      .required("Email is required"),
    phoneNumber: Yup.string()
      .required("Phone number is required")
      .matches(/^\d{10,15}$/, "Please enter a valid phone number"),
    company: Yup.string().required("Company name is required"),
    location: Yup.string().required("Location is required"),
    message: Yup.string(), 
    recaptcha: Yup.boolean()
      .oneOf([true], "Please complete the CAPTCHA")
      .required("CAPTCHA is required"),
  });
  9
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      company: "",
      location: "",
      message: "",
      recaptcha: false,
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      let payload = {
        data: {
          first_name: values.firstName,
          last_name: values.lastName,
          email: values.email,
          company_name: values.company,
          phone_number: values.phoneNumber,
          location: values.location,
          message: values.message,
        },
      };

      try {
        const response = await apiService.postContactUsDetails(payload);
        if (response.status === 200) {
          await apiService.sendThankyouMail({ data: payload.data });
          setSubmissionSuccessful(true);
          setTimeout(() => {
            router.push("/thankyou");
          }, 900);
          resetForm();
        } else {
          setSubmissionSuccessful(false);
        }
      } catch (error) {
        console.log(error);
        setSubmissionSuccessful(false);
      }
    },
  });

  const onChange = (value) => {
    formik.setFieldValue("recaptcha", !!value);
  };

  const sitekey = "6LeeWb0qAAAAAJO25ItXI05RC2T4q6AnlDaKKcIk";

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="contactform_container"
    >
      <div className="contactform_container_row">
        <TextInput
          label={"First name"}
          placeholder={"Enter your first name"}
          id="firstName"
          name="firstName"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorText={formik.touched.firstName && formik.errors.firstName}
          isRequired={true}
        />
        <TextInput
          label={"Last name"}
          placeholder={"Enter your last name"}
          id="lastName"
          name="lastName"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorText={formik.touched.lastName && formik.errors.lastName}
          isRequired={true}
        />
      </div>
      <div className="contactform_container_row">
        <TextInput
          label={"Email address"}
          placeholder={"Enter your email"}
          id="email"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorText={formik.touched.email && formik.errors.email}
          isRequired={true}
        />
        <div className="custom_number_container">
          <p className="input-label">
            Phone number<span style={{ color: "red" }}> *</span>
          </p>
          <PhoneInput
            country={"in"}
            name="phoneNumber"
            id="phoneNumber"
            inputProps={{
              name: "phoneNumber",
              id: "phoneNumber",
            }}
            copyNumbersOnly
            enableSearch
            value={formik.values.phoneNumber}
            onChange={(value) => {
              formik.setFieldValue("phoneNumber", value);
            }}
          />
          {formik.touched.phoneNumber && formik.errors.phoneNumber && (
            <p className="error-message">{formik.errors.phoneNumber}</p>
          )}
        </div>
      </div>
      <div className="contactform_container_row">
        <TextInput
          label={"Company name"}
          placeholder={"Enter your company name"}
          id="company"
          name="company"
          value={formik.values.company}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorText={formik.touched.company && formik.errors.company}
          isRequired={true}
        />
        <TextInput
          label={"Location"}
          placeholder={"Enter your location"}
          id="location"
          name="location"
          value={formik.values.location}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorText={formik.touched.location && formik.errors.location}
          isRequired={true}
        />
      </div>
      <div className="contactform_container_row">
        <TextInput
          label={"Message"}
          placeholder={"Tell us about your requirements"}
          id="message"
          name="message"
          value={formik.values.message}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorText={formik.touched.message && formik.errors.message}
          multi={true}
          isRequired={false}
        />
      </div>
      <div className="contactform_container_row contactform_container_row_bottom">
        <div className="contactform_recaptcha_container">
          <ReCAPTCHA sitekey={sitekey} ref={recaptchaRef} onChange={onChange} />
          {formik.touched.recaptcha && formik.errors.recaptcha && (
            <p className="error-message">{formik.errors.recaptcha}</p>
          )}
        </div>
        <div>
          <CustomButton
            isLoading={formik.isSubmitting}
            Text={
              !formik.isSubmitting && formik.isValid && submissionSuccessful
                ? "Submitted"
                : "Submit"
            }
            PrimaryColorBtn
            type="submit"
          />
        </div>
      </div>
    </form>
  );
};

const TextInput = ({
  errorText,
  label,
  placeholder,
  value,
  onChange,
  multi,
  isRequired = true,
  ...props
}) => {
  return (
    <div className="input-container">
      <label htmlFor="input-field">
        {label}
        {isRequired && <span style={{ color: "red" }}> *</span>}
      </label>
      {multi ? (
        <textarea
          {...props}
          type="text"
          id="input-field"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          cols="30"
          rows="7"
        />
      ) : (
        <input
          {...props}
          type="text"
          id="input-field"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
      )}
      {errorText && <p className="error-message">{errorText}</p>}
    </div>
  );
};

export default ContactForm;
